import React, { useState, useEffect, useRef, useMemo } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  LayoutDashboard,
  Ticket,
  Users,
  DollarSign,
  Settings,
  LogOut,
  Plus,
  Trash2,
  Check,
  Clock,
  ChevronDown,
  Search,
  X,
  QrCode,
  RotateCcw,
  Euro,
  Archive,
  Camera,
  CheckCircle,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Edit,
  Download,
  FileSpreadsheet,
  FileText,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Tag,
  FormInput,
  Calendar,
  Mail,
  Send,
  Eye,
  Menu,
  Paperclip,
  Wrench,
  Lock,
  Shield,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Switch } from "../../components/ui/switch";
import { useAuth } from "../../context/AuthContext";
import { Html5Qrcode } from "html5-qrcode";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { SessionManagement, CouponManagement, CustomFieldsManagement } from "../../components/AdminSettings";
import RichTextEditor from "../../components/RichTextEditor";
import { DisplaySettings } from "./DisplaySettings";
import { ResetEventWizard } from "../../components/admin/ResetEventWizard";
import { FinanceSettings } from "../../components/admin/FinanceSettings";
import { TicketEmailCustomiser } from "../../components/admin/TicketEmailCustomiser";
import { archiveEventToZip } from "../../lib/archiveEvent";
import { InactiveLandingSettings } from "../../components/admin/InactiveLandingSettings";
import {
  SettingsPasswordDialog,
  isSettingsUnlocked,
  lockSettings,
} from "../../components/admin/SettingsPasswordGate";

import { API_URL } from "../../config";

export const SettingsPage = () => {
  const [show, setShow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [archiveStatus, setArchiveStatus] = useState("");
  const [resetWizardOpen, setResetWizardOpen] = useState(false);
  const [unlocked, setUnlocked] = useState(isSettingsUnlocked());
  const [gateOpen, setGateOpen] = useState(!isSettingsUnlocked());
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "show");

  // Inline switch that toggles a `show_*` boolean field on the `show` object.
  // Matches the "Público" stacked-label pattern used across the session/ticket forms.
  const PublicSwitch = ({ field, testid }) => (
    <div className="flex flex-col items-center gap-1 pb-1.5">
      <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Público</span>
      <Switch
        checked={show?.[field] !== false}
        onCheckedChange={(c) => setShow({ ...show, [field]: c })}
        className="data-[state=checked]:bg-violet-500"
        data-testid={testid || `toggle-${field}`}
      />
    </div>
  );

  // Sync tab when URL changes (e.g., user clicks sidebar item "Salas, Sessões e Bilhetes")
  useEffect(() => {
    const t = new URLSearchParams(location.search).get("tab");
    if (t && t !== activeTab) setActiveTab(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const handleLockSettings = () => {
    lockSettings();
    setUnlocked(false);
    toast.success("Configurações bloqueadas");
    navigate("/admin");
  };

  const handleArchiveEvent = async () => {
    if (!window.confirm(
      "Vai descarregar um ficheiro ZIP com TODAS as informações do evento. Continuar?"
    )) return;
    setArchiving(true);
    try {
      const name = await archiveEventToZip(setArchiveStatus);
      toast.success(`Arquivo "${name}.zip" descarregado`);
    } catch (e) {
      toast.error("Erro ao gerar arquivo: " + (e?.message || ""));
    } finally {
      setArchiving(false);
      setArchiveStatus("");
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const showRes = await axios.get(`${API_URL}/api/admin/show`, { withCredentials: true });
      setShow(showRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleShowUpdate = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/show`, show, { withCredentials: true });
      toast.success("Informações atualizadas com sucesso!");
    } catch (error) {
      toast.error("Erro ao atualizar informações");
    } finally {
      setSaving(false);
    }
  };

  const convertImageUrl = (url) => {
    if (!url) return url;
    // Google Drive: /file/d/{ID}/view... or /open?id={ID}
    const driveMatch = url.match(/drive\.google\.com\/file\/d\/([^/]+)/);
    if (driveMatch) return `https://drive.google.com/uc?export=view&id=${driveMatch[1]}`;
    const driveOpenMatch = url.match(/drive\.google\.com\/open\?id=([^&]+)/);
    if (driveOpenMatch) return `https://drive.google.com/uc?export=view&id=${driveOpenMatch[1]}`;
    // Box.com: /s/{ID} → proxy through backend
    const boxMatch = url.match(/app\.box\.com\/s\/([^?/]+)/);
    if (boxMatch) return url; // keep original, will use proxy
    return url;
  };

  const handleImageUrl = (field, rawUrl) => {
    const converted = convertImageUrl(rawUrl.trim());
    if (converted !== rawUrl.trim()) {
      toast.success("Link convertido automaticamente para URL directa");
    }
    setShow({ ...show, [field]: converted });
  };

  if (loading) {
    return <div className="animate-pulse">A carregar...</div>;
  }

  // ──── Password Gate (re-confirmation before showing settings) ────
  if (!unlocked) {
    return (
      <div data-testid="settings-page-locked">
        <div className="bg-zinc-900 rounded-xl border border-amber-500/30 p-8 max-w-md mx-auto text-center mt-12">
          <div className="w-14 h-14 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto mb-4">
            <Lock className="w-7 h-7 text-amber-400" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Configurações Bloqueadas</h2>
          <p className="text-zinc-400 text-sm mb-5">
            Esta área é protegida por password. Confirme a sua password de
            administrador para continuar.
          </p>
          <Button
            onClick={() => setGateOpen(true)}
            className="btn-primary w-full"
            data-testid="settings-unlock-btn"
          >
            <Lock className="w-4 h-4 mr-2" />
            Desbloquear Configurações
          </Button>
        </div>

        <SettingsPasswordDialog
          open={gateOpen}
          onOpenChange={(v) => {
            setGateOpen(v);
            if (!v && !isSettingsUnlocked()) {
              // User cancelled — go back to the dashboard
              navigate("/admin");
            }
          }}
          onSuccess={() => {
            setUnlocked(true);
            setGateOpen(false);
          }}
        />
      </div>
    );
  }

  return (
    <div data-testid="settings-page">
      <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-white">Gestão de Configurações</h1>
          <p className="text-zinc-500 text-sm mt-1">Gerir Todas as Configurações da Plataforma</p>
        </div>
        <Button
          variant="outline"
          onClick={handleLockSettings}
          className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10 hover:text-amber-200"
          data-testid="settings-lock-btn"
        >
          <Lock className="w-4 h-4 mr-2" />
          Bloquear Configurações
        </Button>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(v) => {
          setActiveTab(v);
          const url = new URL(window.location.href);
          url.searchParams.set("tab", v);
          window.history.replaceState({}, "", url);
        }}
        className="space-y-6"
      >
        <TabsList className="bg-zinc-900 border border-white/10 flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="show" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
            <Settings className="w-4 h-4 mr-2" />
            Página
          </TabsTrigger>
          <TabsTrigger value="technical" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
            <Wrench className="w-4 h-4 mr-2" />
            Config. Técnica
          </TabsTrigger>
          <TabsTrigger value="sessions" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
            <Calendar className="w-4 h-4 mr-2" />
            Salas, Sessões e Bilhetes
          </TabsTrigger>
          <TabsTrigger value="coupons" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
            <Tag className="w-4 h-4 mr-2" />
            Cupões
          </TabsTrigger>
          <TabsTrigger value="fields" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
            <FormInput className="w-4 h-4 mr-2" />
            Formulário
          </TabsTrigger>
          <TabsTrigger value="display" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
            <QrCode className="w-4 h-4 mr-2" />
            Ecrã Check-In
          </TabsTrigger>
          <TabsTrigger value="finance" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
            <Euro className="w-4 h-4 mr-2" />
            Financeira
          </TabsTrigger>
          <TabsTrigger value="reset" className="data-[state=active]:bg-red-500 data-[state=active]:text-white">
            <RotateCcw className="w-4 h-4 mr-2" />
            Reset
          </TabsTrigger>
        </TabsList>

        {/* ==================== TAB 1: ESPECTÁCULO ==================== */}
        <TabsContent value="show">
          <div className="space-y-6">

            {/* CONFIGURAÇÃO TOPO */}
            <div className="bg-zinc-900 rounded-xl p-6 border border-white/10">
              <h3 className="text-lg font-semibold text-white mb-1">Configuração Topo</h3>
              <p className="text-zinc-500 text-sm mb-5">Informações de Topo da Página</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="flex items-end gap-3">
                    <div className="flex-1">
                      <Label className="text-zinc-300">Subtítulo</Label>
                      <Input value={show?.hero_badge || ""} onChange={(e) => setShow({ ...show, hero_badge: e.target.value })} className="input-dark mt-2" placeholder="Ex: O Musical" data-testid="setting-hero-badge" />
                    </div>
                    <PublicSwitch field="show_hero_badge" />
                  </div>
                  <p className="text-zinc-500 text-xs mt-1">Texto colorido que aparece acima do título</p>
                </div>
                <div>
                  <div className="flex items-end gap-3">
                    <div className="flex-1">
                      <Label className="text-zinc-300">Título</Label>
                      <Input value={show?.name || ""} onChange={(e) => setShow({ ...show, name: e.target.value })} className="input-dark mt-2" data-testid="setting-show-name" />
                    </div>
                    <PublicSwitch field="show_hero_title" />
                  </div>
                </div>
                <div className="md:col-span-2">
                  <div className="flex items-end gap-3">
                    <div className="flex-1">
                      <Label className="text-zinc-300">Descrição Curta</Label>
                      <textarea value={show?.description || ""} onChange={(e) => setShow({ ...show, description: e.target.value })} rows={2} className="input-dark mt-2 w-full rounded-md p-3" placeholder="Breve descrição do espectáculo (1-2 frases)" data-testid="setting-description-short" />
                    </div>
                    <PublicSwitch field="show_hero_description" />
                  </div>
                  <p className="text-zinc-500 text-xs mt-1">Texto visível antes de clicar em "ver mais"</p>
                </div>
                <div>
                  <div className="flex items-end gap-3">
                    <div className="flex-1">
                      <Label className="text-zinc-300">Local</Label>
                      <Input value={show?.location || ""} onChange={(e) => setShow({ ...show, location: e.target.value })} className="input-dark mt-2" />
                    </div>
                    <PublicSwitch field="show_hero_location" />
                  </div>
                </div>
                <div>
                  <div className="flex items-end gap-3">
                    <div className="flex-1">
                      <Label className="text-zinc-300">Morada</Label>
                      <Input value={show?.address || ""} onChange={(e) => setShow({ ...show, address: e.target.value })} className="input-dark mt-2" />
                    </div>
                    <PublicSwitch field="show_hero_address" />
                  </div>
                  <p className="text-zinc-500 text-xs mt-1">Aparece como sub-linha por baixo do Local na página pública</p>
                </div>
                <div>
                  <div className="flex items-end gap-3">
                    <div className="flex-1">
                      <Label className="text-zinc-300">Texto Sessões (Topo)</Label>
                      <Input value={show?.hero_sessions_text || ""} onChange={(e) => setShow({ ...show, hero_sessions_text: e.target.value })} className="input-dark mt-2" placeholder="2 Sessões Disponíveis" data-testid="setting-hero-sessions-text" />
                    </div>
                    <PublicSwitch field="show_hero_sessions_text" />
                  </div>
                </div>
                <div>
                  <div className="flex items-end gap-3">
                    <div className="flex-1">
                      <Label className="text-zinc-300">Subtítulo Sessões (Topo)</Label>
                      <Input value={show?.hero_sessions_subtitle || ""} onChange={(e) => setShow({ ...show, hero_sessions_subtitle: e.target.value })} className="input-dark mt-2" placeholder="Pode escolher o melhor horário" data-testid="setting-hero-sessions-subtitle" />
                    </div>
                    <PublicSwitch field="show_hero_sessions_subtitle" />
                  </div>
                </div>
                <div>
                  <div className="flex items-end gap-3">
                    <div className="flex-1">
                      <Label className="text-zinc-300">Texto Preço (Topo)</Label>
                      <Input value={show?.hero_price_text || ""} onChange={(e) => setShow({ ...show, hero_price_text: e.target.value })} className="input-dark mt-2" placeholder="A partir de" data-testid="setting-hero-price-text" />
                    </div>
                    <PublicSwitch field="show_hero_price_text" />
                  </div>
                </div>
                <div>
                  <div className="flex items-end gap-3">
                    <div className="flex-1">
                      <Label className="text-zinc-300">Preço Base (€)</Label>
                      <Input type="number" step="0.01" value={show?.price || ""} onChange={(e) => setShow({ ...show, price: parseFloat(e.target.value) })} className="input-dark mt-2" data-testid="setting-show-price" />
                    </div>
                    <PublicSwitch field="show_hero_price" />
                  </div>
                  <p className="text-zinc-500 text-xs mt-1">Preço de referência (os tipos de bilhete têm preços próprios)</p>
                </div>
                <div className="md:col-span-2">
                  <div className="flex items-end gap-3">
                    <div className="flex-1">
                      <Label className="text-zinc-300">Botão Principal - Texto</Label>
                      <Input value={show?.cta_button_text || ""} onChange={(e) => setShow({ ...show, cta_button_text: e.target.value })} className="input-dark mt-2" placeholder="Comprar Bilhete" data-testid="setting-cta-button" />
                    </div>
                    <PublicSwitch field="show_hero_cta" />
                  </div>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-zinc-300">URL da Imagem da Secção Topo <span className="text-amber-400 font-normal">(1080px × 1080px)</span></Label>
                  <Input value={show?.image_url || ""} onChange={(e) => handleImageUrl("image_url", e.target.value)} className="input-dark mt-2" data-testid="setting-hero-image" placeholder="Cole um link direto, Google Drive ou Box.com" />
                  <p className="text-zinc-500 text-xs mt-1">Aceita links diretos, Google Drive e Box.com · proporção 1:1 (quadrada)</p>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-zinc-300">URL da Imagem da Secção Topo Mobile <span className="text-amber-400 font-normal">(1080px × 1080px)</span></Label>
                  <Input value={show?.hero_image_mobile_url || ""} onChange={(e) => handleImageUrl("hero_image_mobile_url", e.target.value)} className="input-dark mt-2" data-testid="setting-hero-image-mobile" placeholder="Cole um link direto, Google Drive ou Box.com" />
                  <p className="text-zinc-500 text-xs mt-1">Aceita links diretos, Google Drive e Box.com · proporção 1:1 (quadrada). Se vazio, usa a imagem principal.</p>
                </div>
              </div>
            </div>

            {/* CONFIGURAÇÃO MEIO */}
            <div className="bg-zinc-900 rounded-xl p-6 border border-white/10">
              <h3 className="text-lg font-semibold text-white mb-1">Configuração Meio</h3>
              <p className="text-zinc-500 text-sm mb-5">Informações de Meio da Página</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="flex items-end gap-3">
                    <div className="flex-1">
                      <Label className="text-zinc-300">Subtítulo</Label>
                      <Input value={show?.about_badge || ""} onChange={(e) => setShow({ ...show, about_badge: e.target.value })} className="input-dark mt-2" placeholder="Sobre o Espectáculo" data-testid="setting-about-badge" />
                    </div>
                    <PublicSwitch field="show_about_badge" />
                  </div>
                  <p className="text-zinc-500 text-xs mt-1">Texto pequeno colorido acima do título</p>
                </div>
                <div>
                  <div className="flex items-end gap-3">
                    <div className="flex-1">
                      <Label className="text-zinc-300">Título</Label>
                      <Input value={show?.about_title || ""} onChange={(e) => setShow({ ...show, about_title: e.target.value })} className="input-dark mt-2" placeholder="Uma Experiência Inesquecível" data-testid="setting-about-title" />
                    </div>
                    <PublicSwitch field="show_about_title" />
                  </div>
                </div>
                <div className="md:col-span-2">
                  <div className="flex items-start gap-3">
                    <div className="flex-1">
                      <Label className="text-zinc-300">Descrição Completa</Label>
                      <p className="text-zinc-500 text-xs mt-1 mb-2">Texto com formatação. Use os botões para estilizar (negrito, itálico, títulos, listas, etc.)</p>
                      <RichTextEditor value={show?.about_description_html || ""} onChange={(html) => setShow({ ...show, about_description_html: html })} placeholder="Escreva aqui a descrição completa do espectáculo..." />
                    </div>
                    <div className="pt-1"><PublicSwitch field="show_about_description" /></div>
                  </div>
                </div>
                <div>
                  <div className="flex items-end gap-3">
                    <div className="flex-1">
                      <Label className="text-zinc-300">Botão "Mais Informações" - Texto</Label>
                      <Input value={show?.about_info_button_text || ""} onChange={(e) => setShow({ ...show, about_info_button_text: e.target.value })} className="input-dark mt-2" placeholder="Mais Informações" data-testid="setting-about-info-button-text" />
                    </div>
                    <PublicSwitch field="show_about_info_button" />
                  </div>
                  <p className="text-zinc-500 text-xs mt-1">⚠️ Tem de estar preenchido para o botão aparecer na página. Deixe vazio para esconder.</p>
                </div>
                <div>
                  <Label className="text-zinc-300">Botão "Mais Informações" - Link</Label>
                  <Input value={show?.about_info_button_url || ""} onChange={(e) => setShow({ ...show, about_info_button_url: e.target.value })} className="input-dark mt-2" placeholder="https://..." data-testid="setting-about-info-button-url" />
                </div>
                <div>
                  <div className="flex items-end gap-3">
                    <div className="flex-1">
                      <Label className="text-zinc-300">Título do Link "Termos e Condições"</Label>
                      <Input
                        value={show?.terms_label || ""}
                        onChange={(e) => setShow({ ...show, terms_label: e.target.value })}
                        className="input-dark mt-2"
                        placeholder="Termos e Condições"
                        data-testid="setting-terms-label"
                      />
                    </div>
                    <PublicSwitch field="show_about_terms" />
                  </div>
                  <p className="text-zinc-500 text-xs mt-1">Texto a mostrar na secção "Sobre" (ex: "Regras e Normas", "Política", etc.)</p>
                </div>
                <div>
                  <Label className="text-zinc-300">Link Termos e Condições</Label>
                  <Input value={show?.terms_url || ""} onChange={(e) => setShow({ ...show, terms_url: e.target.value })} className="input-dark mt-2" placeholder="https://..." data-testid="setting-terms-url" />
                  <p className="text-zinc-500 text-xs mt-1">Link de destino quando o utilizador clica no título acima.</p>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-zinc-300">Média da Secção Meio <span className="text-amber-400 font-normal">(imagem: 1080px × 1080px)</span></Label>
                  <div className="flex items-center gap-4 mt-2 mb-3">
                    <button
                      onClick={() => setShow({ ...show, about_media_type: "image" })}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${(!show?.about_media_type || show?.about_media_type === "image") ? "bg-amber-500 text-black" : "bg-zinc-800 text-zinc-400 border border-white/10"}`}
                      data-testid="media-type-image"
                    >
                      Imagem
                    </button>
                    <button
                      onClick={() => setShow({ ...show, about_media_type: "video" })}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${show?.about_media_type === "video" ? "bg-amber-500 text-black" : "bg-zinc-800 text-zinc-400 border border-white/10"}`}
                      data-testid="media-type-video"
                    >
                      Vídeo YouTube
                    </button>
                  </div>
                  {(!show?.about_media_type || show?.about_media_type === "image") && (
                    <>
                      <Input value={show?.about_image_url || ""} onChange={(e) => handleImageUrl("about_image_url", e.target.value)} className="input-dark" placeholder="Cole um link direto, Google Drive ou Box.com" data-testid="setting-about-image" />
                      <p className="text-zinc-500 text-xs mt-1">Aceita links diretos, Google Drive e Box.com · proporção 1:1 recomendada</p>
                    </>
                  )}
                  {show?.about_media_type === "video" && (
                    <>
                      <Input value={show?.about_video_url || ""} onChange={(e) => setShow({ ...show, about_video_url: e.target.value })} className="input-dark" placeholder="https://www.youtube.com/watch?v=..." data-testid="setting-about-video" />
                      <p className="text-zinc-500 text-xs mt-1">Cole o link do YouTube (ex: https://www.youtube.com/watch?v=XXXXX)</p>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Configuração Bilhetes — textos da secção de compra na landing */}
            <div className="bg-zinc-900 rounded-xl p-6 border border-white/10">
              <h3 className="text-lg font-semibold text-white mb-1">Configuração Bilhetes</h3>
              <p className="text-zinc-500 text-sm mb-5">Textos da secção de compra de bilhetes na página</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-300">Subtítulo</Label>
                  <Input
                    value={show?.tickets_badge || ""}
                    onChange={(e) => setShow({ ...show, tickets_badge: e.target.value })}
                    className="input-dark mt-2"
                    placeholder="Reserve Já"
                    data-testid="setting-tickets-badge"
                  />
                  <p className="text-zinc-500 text-xs mt-1">Texto pequeno colorido acima do título</p>
                </div>
                <div>
                  <Label className="text-zinc-300">Título</Label>
                  <Input
                    value={show?.tickets_title || ""}
                    onChange={(e) => setShow({ ...show, tickets_title: e.target.value })}
                    className="input-dark mt-2"
                    placeholder="Escolha a Sua Sessão"
                    data-testid="setting-tickets-title"
                  />
                  <p className="text-zinc-500 text-xs mt-1">Título grande da secção (ex: "COMPRE O SEU BILHETE")</p>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-zinc-300">Texto Informativo <span className="text-zinc-500 text-xs font-normal">(opcional)</span></Label>
                  <p className="text-zinc-500 text-xs mt-1 mb-2">
                    Texto curto que aparece debaixo do subtítulo na Landing Page. Útil para indicações importantes
                    (ex: "Use sempre o mesmo email", "Cada bilhete é obrigatório por questões legais", etc.).
                  </p>
                  <RichTextEditor
                    value={show?.tickets_info_html || ""}
                    onChange={(html) => setShow({ ...show, tickets_info_html: html })}
                    placeholder="Escreva aqui informações importantes para o comprador..."
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <div className="md:col-span-3 flex items-end gap-3 p-3 rounded-lg bg-violet-500/5 border border-violet-500/20">
                  <div className="flex-1">
                    <Label className="text-zinc-300">Passo 0 (Sala) <span className="text-zinc-500 text-xs">— só aparece se houver salas</span></Label>
                    <Input
                      value={show?.rooms_step_title || ""}
                      onChange={(e) => setShow({ ...show, rooms_step_title: e.target.value })}
                      className="input-dark mt-2"
                      placeholder="Escolha a Sala"
                      data-testid="setting-rooms-step-title"
                    />
                    <p className="text-zinc-500 text-xs mt-1">Cabeçalho do passo de selecção de sala (mostrado acima dos cards de salas)</p>
                  </div>
                  <div className="flex flex-col items-center gap-1 pb-7">
                    <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Público</span>
                    <Switch
                      checked={show?.rooms_step_show_public !== false}
                      onCheckedChange={(c) => setShow({ ...show, rooms_step_show_public: c })}
                      className="data-[state=checked]:bg-violet-500"
                      data-testid="setting-rooms-step-toggle"
                    />
                  </div>
                </div>
                <div className="md:col-span-3 flex items-end gap-3 p-3 rounded-lg bg-violet-500/5 border border-violet-500/20">
                  <div className="flex-1">
                    <Label className="text-zinc-300">Subtítulo "Escolha a Sessão" <span className="text-zinc-500 text-xs">— sub-label acima das sessões</span></Label>
                    <Input
                      value={show?.sessions_step_title || ""}
                      onChange={(e) => setShow({ ...show, sessions_step_title: e.target.value })}
                      className="input-dark mt-2"
                      placeholder="Escolha a Sessão"
                      data-testid="setting-sessions-step-title"
                    />
                    <p className="text-zinc-500 text-xs mt-1">Sub-label uppercase visível depois de escolher uma sala</p>
                  </div>
                  <div className="flex flex-col items-center gap-1 pb-7">
                    <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Público</span>
                    <Switch
                      checked={show?.sessions_step_show_public !== false}
                      onCheckedChange={(c) => setShow({ ...show, sessions_step_show_public: c })}
                      className="data-[state=checked]:bg-violet-500"
                      data-testid="setting-sessions-step-toggle"
                    />
                  </div>
                </div>
                <div className="flex items-end gap-3">
                  <div className="flex-1">
                    <Label className="text-zinc-300">Passo 1 (Sessão)</Label>
                    <Input
                      value={show?.tickets_step1_title || ""}
                      onChange={(e) => setShow({ ...show, tickets_step1_title: e.target.value })}
                      className="input-dark mt-2"
                      placeholder="1. Escolha a Sessão"
                      data-testid="setting-tickets-step1"
                    />
                    <p className="text-zinc-500 text-xs mt-1">Cabeçalho do passo de selecção de sessão</p>
                  </div>
                  <div className="flex flex-col items-center gap-1 pb-7">
                    <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Público</span>
                    <Switch
                      checked={show?.tickets_step1_show_public !== false}
                      onCheckedChange={(c) => setShow({ ...show, tickets_step1_show_public: c })}
                      className="data-[state=checked]:bg-violet-500"
                      data-testid="setting-tickets-step1-toggle"
                    />
                  </div>
                </div>
                <div className="flex items-end gap-3">
                  <div className="flex-1">
                    <Label className="text-zinc-300">Passo 2 (Tipo de Bilhete)</Label>
                    <Input
                      value={show?.tickets_step2_title || ""}
                      onChange={(e) => setShow({ ...show, tickets_step2_title: e.target.value })}
                      className="input-dark mt-2"
                      placeholder="2. Escolha o Tipo de Bilhete"
                      data-testid="setting-tickets-step2"
                    />
                    <p className="text-zinc-500 text-xs mt-1">Cabeçalho do passo de selecção do tipo</p>
                  </div>
                  <div className="flex flex-col items-center gap-1 pb-7">
                    <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Público</span>
                    <Switch
                      checked={show?.tickets_step2_show_public !== false}
                      onCheckedChange={(c) => setShow({ ...show, tickets_step2_show_public: c })}
                      className="data-[state=checked]:bg-violet-500"
                      data-testid="setting-tickets-step2-toggle"
                    />
                  </div>
                </div>
                <div className="flex items-end gap-3">
                  <div className="flex-1">
                    <Label className="text-zinc-300">Passo 3 (Dados de Registo)</Label>
                    <Input
                      value={show?.tickets_step3_title || ""}
                      onChange={(e) => setShow({ ...show, tickets_step3_title: e.target.value })}
                      className="input-dark mt-2"
                      placeholder="3. Dados de Registo"
                      data-testid="setting-tickets-step3"
                    />
                    <p className="text-zinc-500 text-xs mt-1">Cabeçalho do formulário de registo</p>
                  </div>
                  <div className="flex flex-col items-center gap-1 pb-7">
                    <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Público</span>
                    <Switch
                      checked={show?.tickets_step3_show_public !== false}
                      onCheckedChange={(c) => setShow({ ...show, tickets_step3_show_public: c })}
                      className="data-[state=checked]:bg-violet-500"
                      data-testid="setting-tickets-step3-toggle"
                    />
                  </div>
                </div>
              </div>

              {/* Termo de Responsabilidade Parental (Sprint 7) */}
              <div className="mt-6 pt-5 border-t border-white/5">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-cyan-400 text-xs uppercase tracking-[2px] font-semibold flex items-center gap-1.5"><Shield className="w-3.5 h-3.5" /> Termo de Responsabilidade Parental</span>
                </div>
                <p className="text-zinc-500 text-xs mb-3">
                  Texto legal apresentado a encarregados de educação no checkout de bilhetes para menores.
                  Aparecerá num modal antes da assinatura digital e será embutido no PDF enviado por email.
                </p>
                <RichTextEditor
                  value={show?.parental_terms_html || ""}
                  onChange={(html) => setShow({ ...show, parental_terms_html: html, parental_terms_version: (show?.parental_terms_version || 1) + 1 })}
                  placeholder="Eu, encarregado de educação de [nome], autorizo a participação no…"
                  data-testid="setting-parental-terms"
                />
                <p className="text-zinc-500 text-xs mt-1">
                  Versão actual: <strong>v{show?.parental_terms_version || 1}</strong> · Ao guardar com alterações no texto, a versão incrementa automaticamente (importante para auditoria legal).
                </p>
              </div>
            </div>

            {/* CONFIGURAÇÃO RODAPÉ */}
            <div className="bg-zinc-900 rounded-xl p-6 border border-white/10">
              <h3 className="text-lg font-semibold text-white mb-1">Configuração Rodapé</h3>
              <p className="text-zinc-500 text-sm mb-5">Informações de Rodapé da Página</p>
              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="flex-1">
                    <Label className="text-zinc-300">Nome da Organização (canto esquerdo)</Label>
                    <Input value={show?.footer_org_name || ""} onChange={(e) => setShow({ ...show, footer_org_name: e.target.value })} className="input-dark mt-2" placeholder={show?.name || "Ex: KAIROS EVENTOS"} data-testid="setting-footer-org-name" />
                    <p className="text-zinc-500 text-xs mt-1">Se vazio, usa o nome do espectáculo</p>
                  </div>
                  <div className="pt-7 flex items-center gap-2">
                    <Switch checked={show?.show_footer_org_name !== false} onCheckedChange={(checked) => setShow({ ...show, show_footer_org_name: checked })} data-testid="toggle-footer-org-name" />
                    <span className="text-amber-500 text-sm">Visível</span>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="flex-1">
                    <Label className="text-zinc-300">Nome do Local (canto esquerdo)</Label>
                    <Input value={show?.footer_venue_name || ""} onChange={(e) => setShow({ ...show, footer_venue_name: e.target.value })} className="input-dark mt-2" placeholder={show?.location || "Ex: TEATRO MUNICIPAL"} data-testid="setting-footer-venue-name" />
                    <p className="text-zinc-500 text-xs mt-1">Se vazio, usa o local do espectáculo</p>
                  </div>
                  <div className="pt-7 flex items-center gap-2">
                    <Switch checked={show?.show_footer_venue_name !== false} onCheckedChange={(checked) => setShow({ ...show, show_footer_venue_name: checked })} data-testid="toggle-footer-venue-name" />
                    <span className="text-amber-500 text-sm">Visível</span>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-white/5">
                  <div className="flex items-end gap-3">
                    <div className="flex-1">
                      <Label className="text-zinc-300">Texto do Copyright Principal</Label>
                      <Input value={show?.footer_text || ""} onChange={(e) => setShow({ ...show, footer_text: e.target.value })} className="input-dark mt-2" placeholder="Todos os direitos reservados" />
                    </div>
                    <PublicSwitch field="show_footer_text" />
                  </div>
                  <div className="flex items-end gap-3">
                    <div className="flex-1">
                      <Label className="text-zinc-300">Texto do Copyright Adicional</Label>
                      <Input value={show?.footer_copyright || ""} onChange={(e) => setShow({ ...show, footer_copyright: e.target.value })} className="input-dark mt-2" placeholder="Ex: Nome da Empresa" />
                    </div>
                    <PublicSwitch field="show_footer_copyright" />
                  </div>
                </div>
              </div>
            </div>

            <Button onClick={handleShowUpdate} disabled={saving} className="btn-primary" data-testid="save-show-btn">
              {saving ? "A guardar..." : "Guardar Todas as Alterações"}
            </Button>

            {/* ═════════ Página de Entrada (HeyLink-style alternate landing) ═════════ */}
            <InactiveLandingSettings
              show={show}
              setShow={setShow}
              onSave={handleShowUpdate}
              saving={saving}
            />
          </div>
        </TabsContent>

        {/* ==================== TAB 2: CONFIGURAÇÃO TÉCNICA ==================== */}
        <TabsContent value="technical">
          <div className="space-y-6">

            {/* Pagamentos Online */}
            <div className="bg-zinc-900 rounded-xl p-6 border border-white/10">
              <h3 className="text-lg font-semibold text-white mb-1">Pagamentos Online</h3>
              <p className="text-zinc-500 text-sm mb-5">Configuração de métodos de pagamento online</p>
              <div className="space-y-4">

                {/* Caixa Stripe */}
                <div className="p-4 rounded-xl border border-white/10 bg-zinc-800/30">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-white font-semibold">Pagamento por Stripe</p>
                      <p className="text-zinc-500 text-xs">Cartões, MB WAY, Multibanco, PayPal, Wallets, etc.</p>
                    </div>
                    <Switch
                      checked={show?.stripe_enabled || false}
                      onCheckedChange={(checked) => setShow({ ...show, stripe_enabled: checked })}
                      data-testid="toggle-stripe"
                    />
                  </div>
                  {show?.stripe_enabled && (
                    <div className="space-y-3 pt-3 border-t border-white/5">
                      <div className="p-2 rounded-lg bg-green-500/10 border border-green-500/20">
                        <p className="text-green-400 text-xs font-medium">Stripe ativo — os clientes serão redirecionados para o Stripe Checkout após o registo.</p>
                      </div>
                      <div>
                        <Label className="text-zinc-300 mb-2 block">Métodos de Pagamento</Label>
                        <div className="grid grid-cols-2 gap-2">
                          {[
                            { key: "card", label: "Cartão (Visa, Mastercard)" },
                            { key: "multibanco", label: "Multibanco" },
                            { key: "mb_way", label: "MB WAY" },
                            { key: "link", label: "Stripe Link" },
                            { key: "paypal", label: "PayPal" },
                            { key: "revolut_pay", label: "Revolut Pay" },
                            { key: "sepa_debit", label: "Débito SEPA" },
                            { key: "google_pay", label: "Google Pay (auto com Cartão)" },
                            { key: "apple_pay", label: "Apple Pay (auto com Cartão)" },
                          ].map(({ key, label }) => {
                            const methods = show?.stripe_payment_methods || ["card", "multibanco", "mb_way", "link"];
                            const isActive = methods.includes(key);
                            return (
                              <div key={key} className="flex items-center gap-2 p-2 rounded-lg bg-zinc-800/50 border border-white/10">
                                <Switch
                                  checked={isActive}
                                  className="data-[state=checked]:bg-green-500 data-[state=unchecked]:bg-zinc-600"
                                  onCheckedChange={(checked) => {
                                    const current = show?.stripe_payment_methods || ["card", "multibanco", "mb_way", "link"];
                                    const updated = checked
                                      ? [...current, key]
                                      : current.filter((m) => m !== key);
                                    setShow({ ...show, stripe_payment_methods: updated });
                                  }}
                                  data-testid={`toggle-payment-${key}`}
                                />
                                <span className="text-zinc-300 text-sm">{label}</span>
                              </div>
                            );
                          })}
                        </div>
                        <p className="text-zinc-500 text-xs mt-2">Active/desative os métodos que aparecem no checkout</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Caixa Link Externo */}
                <div className="p-4 rounded-xl border border-white/10 bg-zinc-800/30">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-white font-semibold">Link de Pagamento Externo</p>
                      <p className="text-zinc-500 text-xs">Redireciona para um formulário ou página de pagamento externo</p>
                    </div>
                    <Switch
                      checked={show?.external_payment_enabled || false}
                      onCheckedChange={(checked) => setShow({ ...show, external_payment_enabled: checked })}
                      data-testid="toggle-external-payment"
                    />
                  </div>
                  {show?.external_payment_enabled && (
                    <div className="space-y-3 pt-3 border-t border-white/5">
                      <div>
                        <Label className="text-zinc-300">Título</Label>
                        <Input
                          value={show?.external_payment_title || ""}
                          onChange={(e) => setShow({ ...show, external_payment_title: e.target.value })}
                          className="input-dark mt-2"
                          placeholder="Ex: Pagar por Ifthenpay"
                          data-testid="setting-external-payment-title"
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-300">Descrição</Label>
                        <Input
                          value={show?.external_payment_description || ""}
                          onChange={(e) => setShow({ ...show, external_payment_description: e.target.value })}
                          className="input-dark mt-2"
                          placeholder="Ex: Pagamento por referência multibanco"
                          data-testid="setting-external-payment-description"
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-300">Link do Formulário / Pagamento</Label>
                        <Input
                          value={show?.payment_link || ""}
                          onChange={(e) => setShow({ ...show, payment_link: e.target.value })}
                          className="input-dark mt-2"
                          placeholder="https://..."
                          data-testid="setting-payment-link"
                        />
                      </div>
                      <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                        <p className="text-amber-400 text-xs font-medium">O bilhete só será enviado por email após a confirmação do pagamento pelo sistema.</p>
                      </div>
                    </div>
                  )}
                </div>

              </div>
            </div>

            {/* Taxa Global de Pagamento em Prestações */}
            <div className="bg-zinc-900 rounded-xl p-6 border border-white/10">
              <h3 className="text-lg font-semibold text-white mb-1">Taxa de Pagamento em Prestações</h3>
              <p className="text-zinc-500 text-sm mb-4">Taxa global aplicada ao valor final quando o comprador escolhe pagar em prestações. Pode ser sobrescrita por tipo de bilhete.</p>
              <div className="max-w-sm">
                <Label className="text-zinc-300">Taxa Global (%)</Label>
                <div className="relative mt-2">
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={show?.installment_surcharge_pct ?? 3}
                    onChange={(e) => setShow({ ...show, installment_surcharge_pct: e.target.value === "" ? 0 : parseFloat(e.target.value) })}
                    className="input-dark pr-8"
                    data-testid="setting-installment-surcharge-global"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">%</span>
                </div>
                <p className="text-zinc-500 text-xs mt-2">Default: 3%. Defina 0 para desativar a taxa global. Os bilhetes individuais podem ter um valor próprio diferente.</p>
              </div>
            </div>

            {/* Regras de Checkout */}
            <div className="bg-zinc-900 rounded-xl p-6 border border-white/10">
              <div className="flex items-center justify-between mb-1">
                <div>
                  <h3 className="text-lg font-semibold text-white">Regras de Checkout</h3>
                  <p className="text-zinc-500 text-sm mt-1">Restrições que se aplicam ao formulário público de compra.</p>
                </div>
              </div>
              <div className="mt-4 flex items-start justify-between bg-white/[0.02] rounded-lg p-4 border border-white/5">
                <div className="pr-4">
                  <Label className="text-zinc-100 font-medium">Obrigar conta antes de comprar</Label>
                  <p className="text-zinc-500 text-xs mt-1">Quando ligado, o comprador tem de entrar na sua conta (ou criar uma nova) antes de finalizar a compra. Se desligado, mantém a compra anónima como antes.</p>
                </div>
                <Switch
                  checked={!!show?.require_account_for_purchase}
                  onCheckedChange={(v) => setShow({ ...show, require_account_for_purchase: v })}
                  data-testid="toggle-require-account-for-purchase"
                />
              </div>
            </div>

            {/* Pagamentos Offline */}
            <div className="bg-zinc-900 rounded-xl p-6 border border-white/10">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-lg font-semibold text-white">Pagamentos Offline</h3>
                <Switch
                  checked={show?.offline_enabled || false}
                  onCheckedChange={(checked) => setShow({ ...show, offline_enabled: checked })}
                  data-testid="toggle-offline-enabled"
                />
              </div>
              <p className="text-zinc-500 text-sm mb-5">Configuração de locais de pagamento presencial e quais métodos de pagamento offline</p>
              {show?.offline_enabled && (
              <div className="space-y-4">
                {(show?.offline_locations || []).map((loc, index) => (
                  <div key={index} className="p-4 rounded-lg bg-zinc-800/50 border border-white/10">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1 space-y-3">
                        <div>
                          <Label className="text-zinc-300">Título</Label>
                          <Input
                            value={loc.title || ""}
                            onChange={(e) => {
                              const locs = [...(show.offline_locations || [])];
                              locs[index] = { ...locs[index], title: e.target.value };
                              setShow({ ...show, offline_locations: locs });
                            }}
                            className="input-dark mt-1"
                            placeholder="Ex: No Local do Evento"
                            data-testid={`offline-loc-title-${index}`}
                          />
                        </div>
                        <div>
                          <Label className="text-zinc-300">Formas de Pagamento Disponíveis</Label>
                          <Input
                            value={loc.methods || ""}
                            onChange={(e) => {
                              const locs = [...(show.offline_locations || [])];
                              locs[index] = { ...locs[index], methods: e.target.value };
                              setShow({ ...show, offline_locations: locs });
                            }}
                            className="input-dark mt-1"
                            placeholder="Ex: Dinheiro, MB WAY, Transferência"
                            data-testid={`offline-loc-methods-${index}`}
                          />
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10 ml-3"
                        onClick={() => {
                          const locs = (show.offline_locations || []).filter((_, i) => i !== index);
                          setShow({ ...show, offline_locations: locs });
                        }}
                        data-testid={`remove-offline-loc-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={loc.active !== false}
                        onCheckedChange={(checked) => {
                          const locs = [...(show.offline_locations || [])];
                          locs[index] = { ...locs[index], active: checked };
                          setShow({ ...show, offline_locations: locs });
                        }}
                        data-testid={`toggle-offline-loc-${index}`}
                      />
                      <span className="text-amber-500 text-sm">{loc.active !== false ? "Ativo" : "Inativo"}</span>
                    </div>
                  </div>
                ))}
                <Button
                  onClick={() => {
                    const locs = [...(show?.offline_locations || []), { title: "", methods: "", active: true }];
                    setShow({ ...show, offline_locations: locs });
                  }}
                  className="btn-secondary w-full"
                  data-testid="add-offline-location"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar Local de Pagamento
                </Button>
              </div>
              )}
            </div>

            {/* Email */}
            <div className="bg-zinc-900 rounded-xl p-6 border border-white/10">
              <h3 className="text-lg font-semibold text-white mb-1">Email</h3>
              <p className="text-zinc-500 text-sm mb-5">Configure o serviço de envio de emails (Resend)</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-300">API Key do Resend</Label>
                  <Input
                    type="password"
                    value={show?.resend_api_key || ""}
                    onChange={(e) => setShow({ ...show, resend_api_key: e.target.value })}
                    className="input-dark mt-2"
                    placeholder="re_xxxxxxxxxxxx"
                    data-testid="setting-resend-key"
                  />
                  <p className="text-zinc-500 text-xs mt-1">
                    Obtenha em <a href="https://resend.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">resend.com/api-keys</a>
                  </p>
                </div>
                <div>
                  <Label className="text-zinc-300">Email Remetente</Label>
                  <Input
                    value={show?.sender_email || ""}
                    onChange={(e) => setShow({ ...show, sender_email: e.target.value })}
                    className="input-dark mt-2"
                    placeholder="onboarding@resend.dev"
                    data-testid="setting-sender-email"
                  />
                  <p className="text-zinc-500 text-xs mt-1">Email que aparece como remetente</p>
                </div>
              </div>
              {show?.resend_api_key && (
                <div className="mt-3 p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                  <p className="text-green-400 text-sm font-medium">Email configurado</p>
                  <p className="text-zinc-400 text-xs mt-1">Vá a "Comunicação" no menu para enviar campanhas.</p>
                </div>
              )}
              {!show?.resend_api_key && (
                <div className="mt-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                  <p className="text-amber-400 text-sm font-medium">Email não configurado</p>
                  <p className="text-zinc-400 text-xs mt-1">Os emails de bilhete não serão enviados sem API key.</p>
                </div>
              )}

              {/* ── Ticket Email Customisation ─────────────────────────── */}
              <TicketEmailCustomiser show={show} setShow={setShow} onSave={handleShowUpdate} saving={saving} />
            </div>

            {/* WhatsApp (somente contacto) */}
            <div className="bg-zinc-900 rounded-xl p-6 border border-white/10">
              <h3 className="text-lg font-semibold text-white mb-1">WhatsApp</h3>
              <p className="text-zinc-500 text-sm mb-5">Número de contacto via WhatsApp para os compradores.</p>
              <div>
                <Label className="text-zinc-300">Número de WhatsApp para Chat</Label>
                <Input
                  value={show?.whatsapp_number || ""}
                  onChange={(e) => setShow({ ...show, whatsapp_number: e.target.value })}
                  className="input-dark mt-2"
                  placeholder="+351 912 345 678"
                  data-testid="setting-whatsapp-number"
                />
                <p className="text-zinc-500 text-xs mt-1">Inclua o indicativo do país (ex: +351 para Portugal)</p>
              </div>

              {/* Floating WhatsApp support button */}
              <div className="mt-5 pt-5 border-t border-white/5">
                <div className="flex items-center justify-between mb-1">
                  <div>
                    <h4 className="text-white font-semibold">Botão Flutuante de Suporte</h4>
                    <p className="text-zinc-500 text-sm">Mostra um botão verde de WhatsApp em todas as páginas públicas (excluindo admin) para o comprador tirar dúvidas. Usa o número configurado acima.</p>
                  </div>
                  <Switch
                    checked={!!show?.whatsapp_float_enabled}
                    onCheckedChange={(v) => setShow({ ...show, whatsapp_float_enabled: v })}
                    data-testid="toggle-whatsapp-float"
                  />
                </div>
                {show?.whatsapp_float_enabled && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div>
                      <Label className="text-zinc-300">Texto da Tooltip</Label>
                      <Input
                        value={show?.whatsapp_float_tooltip || ""}
                        onChange={(e) => setShow({ ...show, whatsapp_float_tooltip: e.target.value })}
                        className="input-dark mt-2"
                        placeholder="Em caso de dúvida envie mensagem"
                        data-testid="setting-whatsapp-float-tooltip"
                      />
                      <p className="text-zinc-500 text-xs mt-1">Texto que aparece ao lado do botão quando passa o rato ou após 1.5s da página carregar.</p>
                    </div>
                    <div>
                      <Label className="text-zinc-300">Mensagem Pré-Preenchida</Label>
                      <Input
                        value={show?.whatsapp_float_message || ""}
                        onChange={(e) => setShow({ ...show, whatsapp_float_message: e.target.value })}
                        className="input-dark mt-2"
                        placeholder="Olá, tenho uma dúvida sobre os bilhetes..."
                        data-testid="setting-whatsapp-float-message"
                      />
                      <p className="text-zinc-500 text-xs mt-1">A mensagem é aberta no WhatsApp pronta a enviar. Pode usar <code className="text-amber-400">{"{evento}"}</code> para inserir o nome do evento.</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Notificações de Novas Vendas (WhatsApp + Email) */}
            <div className="bg-zinc-900 rounded-xl p-6 border border-white/10">
              <h3 className="text-lg font-semibold text-white mb-1">Notificações de Novas Vendas</h3>
              <p className="text-zinc-500 text-sm mb-5">Receba avisos sempre que alguém compra um bilhete — por WhatsApp, Email ou ambos.</p>

              {/* WhatsApp */}
              <div>
                <h4 className="text-white font-semibold mb-1">Por WhatsApp</h4>
                <p className="text-zinc-500 text-sm mb-4">Mensagem instantânea no WhatsApp via CallMeBot.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-300">Número para Receber Notificações</Label>
                    <Input
                      value={show?.notification_whatsapp_number || ""}
                      onChange={(e) => setShow({ ...show, notification_whatsapp_number: e.target.value })}
                      className="input-dark mt-2"
                      placeholder="+351912345678"
                      data-testid="setting-notification-whatsapp-number"
                    />
                    <p className="text-zinc-500 text-xs mt-1">Apenas dígitos com indicativo (ex: +351912345678)</p>
                  </div>
                  <div>
                    <Label className="text-zinc-300">CallMeBot API Key</Label>
                    <Input
                      value={show?.notification_whatsapp_api_key || ""}
                      onChange={(e) => setShow({ ...show, notification_whatsapp_api_key: e.target.value })}
                      className="input-dark mt-2"
                      placeholder="ex: 1234567"
                      data-testid="setting-notification-whatsapp-api-key"
                    />
                    <p className="text-zinc-500 text-xs mt-1">
                      <a href="https://www.callmebot.com/blog/free-api-whatsapp-messages/" target="_blank" rel="noopener noreferrer" className="text-amber-500 hover:underline">Como obter a API Key ↗</a>
                    </p>
                  </div>
                </div>
                <div className="mt-4 flex gap-3 items-center">
                  <Switch
                    checked={!!show?.notification_whatsapp_enabled}
                    onCheckedChange={(v) => setShow({ ...show, notification_whatsapp_enabled: v })}
                    data-testid="toggle-notification-whatsapp"
                  />
                  <span className="text-zinc-300 text-sm">Ativar notificações WhatsApp</span>
                </div>
                <Button
                  onClick={async () => {
                    try {
                      await axios.post(`${API_URL}/api/admin/test-whatsapp-notification`, {}, { withCredentials: true });
                      toast.success("Mensagem de teste enviada! Verifique o seu WhatsApp.");
                    } catch (err) {
                      toast.error(err.response?.data?.detail || "Erro ao enviar teste");
                    }
                  }}
                  variant="outline"
                  className="mt-4 border-amber-500/40 text-amber-400 hover:bg-amber-500/10"
                  data-testid="btn-test-whatsapp-notification"
                >
                  Enviar Mensagem de Teste
                </Button>
              </div>

              {/* Email (backup / fallback) */}
              <div className="mt-6 pt-6 border-t border-white/10">
                <h4 className="text-white font-semibold mb-1">Por Email</h4>
                <p className="text-zinc-500 text-sm mb-4">
                  Recebe também um email a cada venda paga — entrega imediata, independente do CallMeBot.
                  Ideal como <strong>backup</strong> do WhatsApp.
                </p>
                <div>
                  <Label className="text-zinc-300">Email(s) para Receber Notificações</Label>
                  <Input
                    value={show?.notification_email_address || ""}
                    onChange={(e) => setShow({ ...show, notification_email_address: e.target.value })}
                    className="input-dark mt-2"
                    placeholder="info@bilhetez.com"
                    data-testid="setting-notification-email-address"
                  />
                  <p className="text-zinc-500 text-xs mt-1">Pode introduzir vários endereços separados por vírgula.</p>
                </div>
                <div className="mt-4 flex gap-3 items-center">
                  <Switch
                    checked={!!show?.notification_email_enabled}
                    onCheckedChange={(v) => setShow({ ...show, notification_email_enabled: v })}
                    data-testid="toggle-notification-email"
                  />
                  <span className="text-zinc-300 text-sm">Ativar notificações por Email</span>
                </div>
                <Button
                  onClick={async () => {
                    try {
                      await axios.post(`${API_URL}/api/admin/test-email-notification`, {}, { withCredentials: true });
                      toast.success("Email de teste enviado! Verifique a sua caixa de entrada.");
                    } catch (err) {
                      toast.error(err.response?.data?.detail || "Erro ao enviar teste");
                    }
                  }}
                  variant="outline"
                  className="mt-4 border-amber-500/40 text-amber-400 hover:bg-amber-500/10"
                  data-testid="btn-test-email-notification"
                >
                  Enviar Email de Teste
                </Button>
              </div>
              <div className="pt-4 mt-2 border-t border-white/5">
                <div className="flex items-start gap-3">
                  <Switch
                    checked={show?.notify_installment_received_enabled !== false}
                    onCheckedChange={(v) => setShow({ ...show, notify_installment_received_enabled: v })}
                    data-testid="toggle-notify-installment-received"
                  />
                  <div>
                    <span className="text-zinc-200 text-sm font-medium">Confirmar cada prestação ao comprador</span>
                    <p className="text-zinc-500 text-xs mt-0.5">
                      Envia email automático ao comprador sempre que uma prestação é registada, com barra de progresso e próxima data. O email da última prestação (QR final) é sempre enviado.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Redes Sociais */}
            <div className="bg-zinc-900 rounded-xl p-6 border border-white/10">
              <h3 className="text-lg font-semibold text-white mb-1">Redes Sociais</h3>
              <p className="text-zinc-500 text-sm mb-5">Links e visibilidade de cada rede social</p>
              <div className="space-y-3">
                {[
                  { key: "instagram", label: "Instagram", placeholder: "https://instagram.com/..." },
                  { key: "facebook", label: "Facebook", placeholder: "https://facebook.com/..." },
                  { key: "youtube", label: "YouTube", placeholder: "https://youtube.com/..." },
                  { key: "twitter", label: "Twitter / X", placeholder: "https://x.com/..." },
                  { key: "tiktok", label: "TikTok", placeholder: "https://tiktok.com/..." },
                  { key: "whatsapp", label: "WhatsApp", placeholder: "https://wa.me/351912345678" },
                  { key: "email", label: "Email", placeholder: "mailto:exemplo@email.com" },
                  { key: "website", label: "Website", placeholder: "https://..." },
                  { key: "custom", label: "Link Personalizado", placeholder: "https://..." },
                ].map(({ key, label, placeholder }) => (
                  <div key={key} className="flex items-center gap-4">
                    <div className="flex-1">
                      <Label className="text-zinc-300">{label}</Label>
                      <Input
                        value={show?.[`social_${key}`] || ""}
                        onChange={(e) => setShow({ ...show, [`social_${key}`]: e.target.value })}
                        className="input-dark mt-1"
                        placeholder={placeholder}
                      />
                    </div>
                    <div className="pt-5 flex items-center gap-2">
                      <Switch
                        checked={show?.[`show_social_${key}`] !== false}
                        onCheckedChange={(checked) => setShow({ ...show, [`show_social_${key}`]: checked })}
                        data-testid={`toggle-social-${key}`}
                      />
                      <span className="text-amber-500 text-sm">Visível</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Cor de Destaque */}
            <div className="bg-zinc-900 rounded-xl p-6 border border-white/10">
              <h3 className="text-lg font-semibold text-white mb-1">Cor de Destaque</h3>
              <p className="text-zinc-500 text-sm mb-5">Cor principal usada em toda a página</p>
              <div className="flex items-end gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={show?.accent_color || "#f59e0b"}
                      onChange={(e) => setShow({ ...show, accent_color: e.target.value })}
                      className="w-12 h-10 rounded cursor-pointer border border-white/10 bg-transparent"
                      data-testid="setting-accent-color-picker"
                    />
                    <Input
                      value={show?.accent_color || "#f59e0b"}
                      onChange={(e) => setShow({ ...show, accent_color: e.target.value })}
                      className="input-dark font-mono w-32"
                      placeholder="#f59e0b"
                      data-testid="setting-accent-color-hex"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  {["#f59e0b", "#ef4444", "#3b82f6", "#10b981", "#8b5cf6", "#ec4899", "#06b6d4", "#f97316"].map((color) => (
                    <button
                      key={color}
                      onClick={() => setShow({ ...show, accent_color: color })}
                      className="w-8 h-8 rounded-full border-2 transition-all hover:scale-110"
                      style={{
                        backgroundColor: color,
                        borderColor: show?.accent_color === color ? "#fff" : "transparent",
                      }}
                      data-testid={`accent-preset-${color.replace('#', '')}`}
                    />
                  ))}
                </div>
                <div
                  className="rounded-lg px-4 py-2 text-sm font-semibold"
                  style={{ backgroundColor: show?.accent_color || "#f59e0b", color: "#000" }}
                >
                  Pré-visualização
                </div>
              </div>
            </div>

            <Button onClick={handleShowUpdate} disabled={saving} className="btn-primary">
              {saving ? "A guardar..." : "Guardar Configurações Técnicas"}
            </Button>
          </div>
        </TabsContent>

        {/* ==================== TAB 3: SESSÕES E BILHETES ==================== */}
        <TabsContent value="sessions">
          <div className="space-y-6">
            <SessionManagement />
          </div>
        </TabsContent>

        {/* ==================== TAB 4: CUPÕES ==================== */}
        <TabsContent value="coupons">
          <CouponManagement />
        </TabsContent>

        {/* ==================== TAB 5: FORMULÁRIO ==================== */}
        <TabsContent value="fields">
          <CustomFieldsManagement />
        </TabsContent>

        {/* ==================== TAB 6: ECRÃ DE CHECK-IN ==================== */}
        <TabsContent value="display">
          <DisplaySettings show={show} setShow={setShow} onSave={handleShowUpdate} saving={saving} />
        </TabsContent>

        {/* ==================== TAB 7: FINANCEIRA ==================== */}
        <TabsContent value="finance">
          <FinanceSettings />
        </TabsContent>

        {/* ==================== TAB 8: RESET ==================== */}
        <TabsContent value="reset">
          <div className="bg-zinc-900 rounded-xl border border-white/10 p-6 space-y-6" data-testid="settings-reset-tab">
            <div>
              <h3 className="text-lg font-semibold text-white">Reset de Evento</h3>
              <p className="text-zinc-400 text-sm mt-1">
                Arquive todas as informações do evento actual e prepare a plataforma para um novo evento.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-zinc-950 rounded-lg border border-amber-500/30 p-5">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center">
                    <Archive className="w-5 h-5 text-amber-400" />
                  </div>
                  <h4 className="text-white font-semibold">Arquivar Evento (ZIP)</h4>
                </div>
                <p className="text-zinc-400 text-sm mb-4">
                  Descarrega um ZIP com TUDO: configurações, sessões, vendas, cupões, finanças, imagens, comunicação. Apenas .xlsx, .pdf, .txt, .jpg.
                </p>
                <Button
                  onClick={handleArchiveEvent}
                  disabled={archiving}
                  className="btn-primary w-full"
                  data-testid="settings-btn-archive"
                >
                  <Archive className={`w-4 h-4 mr-2 ${archiving ? "animate-pulse" : ""}`} />
                  {archiving ? archiveStatus || "A arquivar..." : "Arquivar Evento (ZIP)"}
                </Button>
              </div>

              <div className="bg-zinc-950 rounded-lg border border-red-500/30 p-5">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center">
                    <RotateCcw className="w-5 h-5 text-red-400" />
                  </div>
                  <h4 className="text-white font-semibold">Reset para Novo Evento</h4>
                </div>
                <p className="text-zinc-400 text-sm mb-4">
                  Wizard de 3 passos que força o arquivo, escolha do que apagar, e confirmação textual antes de executar. Configurações são preservadas.
                </p>
                <Button
                  onClick={() => setResetWizardOpen(true)}
                  className="bg-red-600 hover:bg-red-700 text-white w-full"
                  data-testid="settings-btn-reset"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Reset para Novo Evento
                </Button>
              </div>
            </div>

            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 text-sm text-blue-100">
              <p className="font-semibold mb-1">💡 Fluxo recomendado de fim de evento</p>
              <ol className="list-decimal list-inside space-y-1 text-blue-100/90">
                <li>Clique em <strong>Arquivar Evento (ZIP)</strong> e guarde o ficheiro no seu computador / Drive.</li>
                <li>Clique em <strong>Reset para Novo Evento</strong> e siga o wizard.</li>
                <li>Após o reset, ajuste as <strong>Sessões</strong> (datas/horários) e os <strong>Cupões</strong> do novo evento.</li>
                <li>Coloque o site em "Live" outra vez.</li>
              </ol>
            </div>
          </div>

          <ResetEventWizard
            open={resetWizardOpen}
            onClose={() => setResetWizardOpen(false)}
            onComplete={() => fetchData()}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

// QR Code Scanner for Ticket Validation
